/**
 * Integrations Registry - categories and platform → registry id mapping
 */

import type { CategoryDefinition } from './types.js';

// Order is the pill order. Social sits right after Messaging because the two
// overlap (Meta and WhatsApp answer to both); AI sits after Marketing because
// it is the newest thing a merchant comes here looking for.
export const CATEGORIES: CategoryDefinition[] = [
  { id: 'all', labelKey: 'integrations.category.all', icon: 'LayoutGrid' },
  { id: 'messaging', labelKey: 'integrations.category.messaging', icon: 'MessageCircle' },
  { id: 'social', labelKey: 'integrations.category.social', icon: 'Share2' },
  { id: 'ecommerce', labelKey: 'integrations.category.ecommerce', icon: 'ShoppingCart' },
  { id: 'email', labelKey: 'integrations.category.email', icon: 'Mail' },
  { id: 'marketing', labelKey: 'integrations.category.marketing', icon: 'Megaphone' },
  { id: 'ai', labelKey: 'integrations.category.ai', icon: 'Bot' },
  { id: 'accounting', labelKey: 'integrations.category.accounting', icon: 'Calculator' },
  { id: 'support', labelKey: 'integrations.category.support', icon: 'Headphones' },
  { id: 'operations', labelKey: 'integrations.category.operations', icon: 'Settings' },
  // Analytics pill hidden for now — restore by uncommenting when tiles ship.
  // { id: 'analytics', labelKey: 'integrations.category.analytics', icon: 'BarChart3' },
];

/** Map DB platform names to registry IDs */
export const PLATFORM_TO_REGISTRY_IDS: Record<string, string[]> = {
  // `meta_ads` is NOT here, and that is the fix for a badge that lied three
  // separate times in one day's audit. Connecting Facebook Messenger and
  // connecting Meta Ads are different acts — `meta_ads` has its own
  // connectUrl asking for `scope=ads` — but this map implied the second from
  // the first, so a workspace with a Messenger row showed "Meta Ads ·
  // Connected" and the setup checklist ticked "Link your ad account" while
  // `AdCredential` held zero rows. See SCOPE_GATED_REGISTRY_IDS below.
  //
  // `whatsapp_business` is absent for the same reason, and the `whatsapp` key
  // is gone entirely rather than emptied — because this map is consulted TWICE
  // per row, once on the platform and once on the NAME PREFIX, and production's
  // row is called "whatsapp Integration". Leaving the key would have let the
  // name alone re-fabricate the badge with the platform mapping already fixed.
  // See ASSET_GATED_REGISTRY_IDS below.
  facebook: ['facebook_messenger'],
  instagram: ['instagram_dms'],
  meta: ['facebook_messenger', 'instagram_dms'],
  shopify: ['shopify'],
  woocommerce: ['woocommerce'],
  bigcommerce: ['bigcommerce'],
  wix: ['wix'],
  ecwid: ['ecwid'],
  squarespace: ['squarespace'],
  prestashop: ['prestashop'],
  gmail: ['gmail'],
  email_support: ['gmail'],
  // 'resend' has no marketplace card: it is the platform's own ESP, configured
  // server-side, never connected per-merchant.
  cardcom: ['cardcom'],
  hubspot: ['hubspot_crm'],
  salesforce: ['salesforce_crm'],
  'google-sheets': ['google_sheets'],
  google_ads: ['google_ads'],
  'google-ads': ['google_ads'],
  tiktok: ['tiktok_business'],
  tiktok_ads: ['tiktok_ads'],
  'tiktok-ads': ['tiktok_ads'],
  linkedin: ['linkedin_marketing'],
  youtube: ['youtube'],
  // The DB enum value is X; the registry id is x_social, because a one-letter
  // id in a shared registry reads as a typo at every call site.
  x: ['x_social'],
  slack: ['slack'],
  zapier: ['zapier'],
  // Connector-store platforms without a marketplace tile (kind-alignment §2):
  // funnel live/paused pills and setup-checklist steps key off these ids, so a
  // workspace whose Integration row carries the platform must light them up.
  stripe: ['stripe'],
  telegram: ['telegram'],
  community_link: ['community_link'],
  email_sending: ['email_sending'],
};

/**
 * Ids a connection may only claim when the PROVIDER SAYS SO.
 *
 * A platform row proves a connection exists; it does not prove what that
 * connection may do. Meta hands out permissions one at a time, so a Facebook
 * row without an ads permission cannot run ads no matter how green its badge
 * is — and until `Integration.grantedScopes` was recorded there was no way to
 * tell the two apart, which is why the badge was simply assumed.
 *
 * An EMPTY scope list means "we have not asked", not "nothing granted": a
 * connection made before scopes were recorded must not be demoted on a guess,
 * and must not be promoted on one either. It simply does not claim the id.
 */
export const SCOPE_GATED_REGISTRY_IDS: Record<string, { platforms: string[]; anyScope: string[] }> = {
  meta_ads: {
    platforms: ['facebook', 'instagram', 'meta'],
    anyScope: ['ads_read', 'ads_management'],
  },
};

/**
 * Ids a connection may only claim when THE ASSET IS ACTUALLY ATTACHED.
 *
 * This is the sibling of SCOPE_GATED_REGISTRY_IDS and the difference between
 * them matters. A missing scope means "we have not asked" — the provider is
 * the authority and we may simply not have consulted it, so the id is claimed
 * neither way. A missing asset means "nothing is attached", full stop: the
 * field is written by OUR OWN setup flow, so its absence is our own record of
 * a step that never happened, not an unanswered question.
 *
 * WhatsApp is the case that motivated it. An Integration row appears the
 * moment Facebook Login completes; the embedded signup that binds a phone
 * number is a SEPARATE act, and in production it never ran — one row, `active`
 * since 2026-06-14, no `whatsappPhoneNumberId`, no display number, no business
 * account, and zero WhatsApp messages in the product's history. It rendered as
 * a live channel anyway, ticked its own setup-checklist step, and counted
 * toward the funnel pills' Meta messaging group.
 *
 * Every path that actually sends already required the number. This makes the
 * badge agree with them.
 */
export const ASSET_GATED_REGISTRY_IDS: Record<string, { platforms: string[]; requiresField: string }> = {
  whatsapp_business: {
    platforms: ['whatsapp', 'meta'],
    requiresField: 'whatsappPhoneNumberId',
  },
};
